import React, { useEffect, useRef } from 'react'

import { ActionButton, RfShell } from '../components/RfShell'
import { RfStatusBanner } from '../components/RfStatusBanner'
import { receivingContext, ReceivingFlowController } from '../flow'
import { LabelType, labelTypes, labelTypeText, receivingPrinters } from '../labels'
import {
  DecisionActions,
  DecisionButton,
  DeclineButton,
  DecisionDialog,
  DialogBackdrop,
  PrintGroupCard,
  LabelGroupHeading,
  LabelProcessingHeading,
  PrintProgress,
} from '../styles'

interface LabelProcessingScreenProps {
  controller: ReceivingFlowController
  embedded?: boolean
  onExit: () => void
}

export const LabelProcessingScreen = ({ controller, embedded = false, onExit }: LabelProcessingScreenProps) => {
  const { state, dispatch } = controller
  const labels = state.labels ?? []
  const allPrinted = labels.length > 0 && labels.every((label) => label.printed && label.confirmed)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const continueRef = useRef<HTMLButtonElement>(null)
  const buttons = useRef<Partial<Record<LabelType, HTMLButtonElement | null>>>({})
  const lastGroup = useRef<LabelType | null>(null)
  useEffect(() => {
    if (state.printConfirmation) {
      lastGroup.current = state.printConfirmation
      cancelRef.current?.focus()
    } else if (lastGroup.current) {
      const previous = buttons.current[lastGroup.current]
      const target =
        previous && !previous.disabled
          ? previous
          : Object.values(buttons.current).find((button) => button && !button.disabled)
      const focusTarget = target ?? continueRef.current
      focusTarget?.focus()
    }
  }, [state.printConfirmation])
  const unconfirmed = labels.filter(
    (label) => label.type === state.printConfirmation && label.printed && !label.confirmed
  )
  const printDialog = state.printConfirmation ? (
    <DialogBackdrop>
      <DecisionDialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="print-confirmation-title"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            dispatch({ type: 'decline-labels-printed' })
          }
          if (event.key === 'Tab') {
            const controls = dialogRef.current?.querySelectorAll('button')
            if (!controls?.length) return
            if (event.shiftKey && document.activeElement === controls[0]) {
              event.preventDefault()
              controls[controls.length - 1].focus()
            } else if (!event.shiftKey && document.activeElement === controls[controls.length - 1]) {
              event.preventDefault()
              controls[0].focus()
            }
          }
        }}
      >
        <h2 id="print-confirmation-title">Confirm labels printed</h2>
        <p>
          Did all {unconfirmed.length} {labelTypeText(state.printConfirmation)} labels print correctly?
        </p>
        <DecisionActions>
          <DeclineButton ref={cancelRef} type="button" onClick={() => dispatch({ type: 'decline-labels-printed' })}>
            Decline
          </DeclineButton>
          <DecisionButton $primary type="button" onClick={() => dispatch({ type: 'confirm-labels-printed' })}>
            Confirm
          </DecisionButton>
        </DecisionActions>
      </DecisionDialog>
    </DialogBackdrop>
  ) : null

  return (
    <RfShell
      title="Receiving"
      phase="Labels"
      embedded={embedded}
      contextSummary={receivingContext(state)}
      onBack={() => dispatch({ type: 'back' })}
      moreActions={[{ label: 'Exit receiving', onSelect: onExit, danger: true }]}
      overlay={printDialog}
      actions={
        <ActionButton
          ref={continueRef}
          type="button"
          disabled={!allPrinted}
          onClick={() => dispatch({ type: 'continue-to-receiving' })}
        >
          Continue to receiving
        </ActionButton>
      }
    >
      <LabelProcessingHeading>
        <h1>Print labels</h1>
        <p>Printer: {receivingPrinters.find((printer) => printer.id === state.printerId)?.name}</p>
      </LabelProcessingHeading>
      {state.status && state.status.tone !== 'success' ? (
        <RfStatusBanner tone={state.status.tone}>{state.status.message}</RfStatusBanner>
      ) : null}
      {labelTypes.map((type) => {
        const group = labels.filter((label) => label.type === type)
        if (!group.length) return null
        const confirmed = group.filter((label) => label.confirmed).length
        return (
          <PrintGroupCard key={type} aria-label={`${labelTypeText(type)} labels`}>
            <LabelGroupHeading>
              <h2>{labelTypeText(type)}</h2>
              <PrintProgress
                role="status"
                aria-label={`${confirmed} of ${group.length} ${labelTypeText(type)} labels confirmed printed`}
              >
                {confirmed === group.length ? (
                  <>
                    <span className="check" aria-hidden="true">
                      ✓
                    </span>{' '}
                  </>
                ) : null}
                <strong>
                  {confirmed}/{group.length}
                </strong>{' '}
                <span className="label">Printed</span>
              </PrintProgress>
            </LabelGroupHeading>
            {confirmed < group.length ? (
              <ActionButton
                type="button"
                ref={(button) => {
                  buttons.current[type] = button
                }}
                aria-label={`Print ${group.length - confirmed} ${labelTypeText(type)} label${group.length - confirmed === 1 ? '' : 's'}`}
                disabled={!!state.printConfirmation}
                onClick={() => dispatch({ type: 'submit-label-print', labelType: type })}
              >
                Print
              </ActionButton>
            ) : null}
          </PrintGroupCard>
        )
      })}
    </RfShell>
  )
}
